import { useCallback, useState } from 'react'

/**
 * Hook personalizado para gerenciar o estado do formulário de unidade (IoC - 100% desacoplado)
 * Todas as dependências são injetadas via parâmetros
 */
const useUnidadeService = ({initialData = null, useToastService, useWebInterface,endPointCaderno}) => {

  const [formData, setFormData] = useState({
    nome: initialData?.nome || '',
    label: initialData?.label || '',
    fatorSi: initialData?.fatorSi || '',
    unidadeBaseSi: initialData?.unidadeBaseSi || ''
  })

  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [isDirty, setIsDirty] = useState(false)
  const webInterface = useWebInterface({baseEndPoint: endPointCaderno, context : "unidade"})
  const toastService = useToastService()
  // ✅ useToastService obrigatório - sempre deve ser fornecido (mesmo que seja mock)
  if (!useToastService) {
    throw new Error('useUnidadeForm: useToastService é obrigatório. Use um mock se não disponível.')
  }

  // Validação dos campos
  const validate = useCallback(() => {
    const newErrors = {}
    
    if (!formData.nome || formData.nome.trim() === '') {
      newErrors.nome = 'Nome da unidade'
    }
    
    if (!formData.label || formData.label.trim() === '') {
      newErrors.label = 'Sigla'
    }
    
    if (!formData.fatorSi || formData.fatorSi === '' || isNaN(parseFloat(formData.fatorSi))) {
      newErrors.fatorSi = 'Fator de conversão, e deve ser um número válido'
    }
    
    if (!formData.unidadeBaseSi || formData.unidadeBaseSi.trim() === '') {
      newErrors.unidadeBaseSi = 'Unidade base (SI)'
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }, [formData])

  const updateField = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
    setIsDirty(true)
    
    // Limpar erro do campo quando o usuário começar a digitar
    setErrors(prev => {
      if (prev[field]) {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      }
      return prev
    })
  }, [])


  const reset = useCallback(() => {
    setFormData({
      nome: initialData?.nome || '',
      label: initialData?.label || '',
      fatorSi: initialData?.fatorSi || '',
      unidadeBaseSi: initialData?.unidadeBaseSi || ''
    })
    setErrors({})
    setIsDirty(false)
  }, [initialData])

  const submit = useCallback(async (onSuccess) => {
    // Validar antes de submeter
    if (!validate()) {
      if (toastService?.error) {
        toastService.error('Por favor, preencha todos os campos obrigatórios')
      }
      return false
    }

    setIsLoading(true)
    
    try {
      let result
      if (initialData?.id) {
        result = await webInterface.put(`unidade/${initialData.id}`, formData)
        // Mostrar toast apenas se o service tiver toastService configurado
        if (toastService?.success) {
          toastService.success('Unidade atualizada com sucesso!')
        }
      } else {
        result = await webInterface.post(`unidade`, formData)
        // Mostrar toast apenas se o service tiver toastService configurado
        if (toastService?.success) {
          toastService.success('Unidade criada com sucesso!')
        }
      }

      // Limpar o formulário após sucesso
      reset()
      
      if (onSuccess) {
        onSuccess(result)
      }
      
      return true
    } catch (error) {
      console.error('Erro ao salvar unidade:', error)
      // Mostrar toast apenas se o service tiver toastService configurado
      if (toastService?.error) {
        toastService.error('Erro ao salvar unidade. Tente novamente.')
      }
      return false
    } finally {
      setIsLoading(false)
    }
  }, [formData, initialData, toastService, validate, reset, webInterface])

  const remove = useCallback(async (onSuccess) => {
    if (!initialData?.id) return false
    
    setIsLoading(true)
    
    try {
      await webInterface.delete(initialData.id)

      // Mostrar toast apenas se o service tiver toastService configurado
      if (toastService?.success) {
        toastService.success('Unidade removida com sucesso!')
      }
      
      if (onSuccess) {
        onSuccess()
      }
      
      return true
    } catch (error) {
      console.error('Erro ao remover unidade:', error)
      // Mostrar toast apenas se o service tiver toastService configurado
      if (toastService?.error) {
        toastService.error('Erro ao remover unidade. Tente novamente.')
      }
      return false
    } finally {
      setIsLoading(false)
    }
  }, [initialData, toastService, webInterface])

  return {
    formData,
    errors,
    isLoading,
    isDirty,
    updateField,
    validate,
    reset,
    submit,
    remove
  }
}
export default useUnidadeService